import { Typography } from '@vivaahealth/design-system';

import * as React from 'react';

import styles from './index.module.css';
import Footer from '../Footer';
import searchIcon from '../../../../assets/newsearch.png';
import NavBar, { View } from '../../../../components/NavBar';
import { TextInput } from '../../../../components/TextInput';
import InputAdornment from '@material-ui/core/InputAdornment';
import ProviderOption from './ProviderOption';
import { ReasonForVisitEmptyState } from './ReasonForVisitEmptyState';
import ReasonForVisitSuggestion from './ReasonForVisitSuggestion';
import { RouteComponentProps } from 'react-router-dom';
import { useState } from 'react';
import {
  allCategories,
  allProviders,
  ProviderOptionData,
  SearchCategory,
  SearchItem,
} from './data';

interface Props extends RouteComponentProps {}

const ReasonForVisitSelector: React.FC<Props> = (props) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [cursor, setCursor] = useState<number>(-1);
  const [suggestions, setSuggestions] = useState<SearchItem[]>([]);
  const [suggestedProviders, setSuggestedProviders] = useState<
    ProviderOptionData[]
  >([]);
  const [selectedSpecialty, setSelectedSpecialty] = useState<SearchItem | null>(
    null,
  );

  const initialCategories = allCategories();
  const initialProviders = allProviders();

  const allSuggestions = (): SearchItem[] => {
    return initialCategories.flatMap((category) => {
      return category.items;
    });
  };

  const [currentCategories, setCurrentCategories] =
    useState<SearchCategory[]>(initialCategories);

  const updateInputValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.currentTarget.value;
    let suggestions: SearchItem[] = [];
    let categories: SearchCategory[] = [];
    let suggestedDoctors: ProviderOptionData[] = [];
    if (newValue.length > 0) {
      suggestions = allSuggestions().filter((suggestion) => {
        return suggestion.name.toLowerCase().includes(newValue.toLowerCase());
      });
      suggestedDoctors = initialProviders.filter((suggestion) => {
        const name = suggestion.firstName + ' ' + suggestion.lastName;
        return name.toLowerCase().includes(newValue.toLowerCase());
      });
    } else {
      categories = initialCategories;
    }

    setInputValue(newValue);
    setSuggestions(suggestions);
    setCurrentCategories(categories);
    setSuggestedProviders(suggestedDoctors);
    setCursor(-1);
  };

  const handleClick = (item: SearchItem) => {
    setCursor(-1);
    if (
      item.categoryType?.toLowerCase() === 'specialty' &&
      item.subItems &&
      item.subItems.length > 0
    ) {
      setSelectedSpecialty(item);
      setInputValue('');
    } else {
      props.history.push(`/reasonForVisitUserTestingEnd`);
    }
  };

  const handleProviderClick = (provider: ProviderOptionData) => {
    props.history.push(`/reasonForVisitUserTestingEnd`);
  };

  const resultsLength = () => {
    if (selectedSpecialty && selectedSpecialty.subItems) {
      return selectedSpecialty.subItems.length;
    } else if (suggestions.length > 0 || suggestedProviders.length > 0) {
      return suggestions.length + suggestedProviders.length;
    } else {
      return currentCategories.flatMap((category) => category.items).length;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const categories = currentCategories;

    // arrow up/down button should select next/previous list element
    if (e.key.toLowerCase() === 'arrowup' && cursor > 0) {
      return setCursor(cursor - 1);
    } else if (
      e.key.toLowerCase() === 'arrowdown' &&
      cursor < resultsLength() - 1
    ) {
      return setCursor(cursor + 1);
    } else if (e.key.toLowerCase() === 'enter') {
      if (cursor < 0) {
        return;
      }
      if (
        selectedSpecialty &&
        selectedSpecialty.subItems &&
        selectedSpecialty.subItems.length > cursor
      ) {
        const item = selectedSpecialty.subItems[cursor];
        handleClick(item);
      } else if (suggestions.length > 0 || suggestedProviders.length > 0) {
        if (suggestions.length > cursor) {
          const item = suggestions[cursor];
          handleClick(item);
        } else if (suggestions.length + suggestedProviders.length > cursor) {
          const provider = suggestedProviders[cursor - suggestions.length];
          handleProviderClick(provider);
        }
      } else {
        const { itemPosition, categoryIndex } = findCategoryAndItem(cursor);
        if (categoryIndex < categories.length) {
          const category = categories[categoryIndex];
          if (itemPosition < category.items.length) {
            const item = category.items[itemPosition];
            handleClick(item);
          }
        }
      }
    }
  };

  const findCategoryAndItem = (
    cursor: number,
  ): { itemPosition: number; categoryIndex: number } => {
    let categoryIndex = 0;
    let rowIndex = cursor;
    while (rowIndex > 0 && categoryIndex < currentCategories.length) {
      const currentCategoryItemCount =
        currentCategories[categoryIndex].items.length;
      if (rowIndex < currentCategoryItemCount) {
        break;
      }
      rowIndex = rowIndex - currentCategoryItemCount;
      categoryIndex = categoryIndex + 1;
    }
    return { itemPosition: rowIndex, categoryIndex: categoryIndex };
  };

  const suggestionResults = (items: SearchItem[]) => {
    const itemCount = items.length;
    if (itemCount === 0) {
      return <div style={{ marginTop: `1rem` }}></div>;
    }
    return (
      <div className={styles.FilteredResults}>
        {items.map((item, index) => {
          return (
            <ReasonForVisitSuggestion
              key={item.name}
              input={inputValue}
              data={item}
              onClicked={() => handleClick(item)}
              isSelected={
                cursor >= 0 && cursor <= itemCount - 1 && cursor === index
              }
            />
          );
        })}
      </div>
    );
  };

  const specialtyResults = (items: SearchItem[]) => {
    const filteredItems = items.filter((value) => {
      return value.name.toLowerCase().includes(inputValue.toLowerCase());
    });

    if (filteredItems.length > 0) {
      return suggestionResults(filteredItems);
    } else {
      return <ReasonForVisitEmptyState inputString={inputValue} />;
    }
  };

  const providerResults = (providers: ProviderOptionData[]) => {
    if (providers.length === 0) {
      return <></>;
    }
    const providerResults = suggestedProviders.map((suggestion, index) => {
      return (
        <ProviderOption
          key={suggestion.firstName + ' ' + suggestion.lastName}
          prefix={suggestion.prefix}
          firstName={suggestion.firstName}
          lastName={suggestion.lastName}
          suffix={suggestion.suffix}
          specialty={suggestion.specialty}
          location={suggestion.location}
          input={inputValue}
          onClicked={() => handleProviderClick(suggestion)}
          isSelected={cursor === index + suggestions.length}
        />
      );
    });

    return (
      <>
        <div className={styles.ProvidersCategoryLabel}>
          <Typography variant="title-medium" bold>
            Providers
          </Typography>
        </div>
        {providerResults}
      </>
    );
  };

  const initialResults = () => {
    const { itemPosition, categoryIndex } = findCategoryAndItem(cursor);

    const results = currentCategories.map((category, index) => {
      return (
        <div key={category.name}>
          <div className={styles.CategoryName}>
            <Typography variant="title-medium" bold>
              {category.name}
            </Typography>
          </div>
          {category.items.map((item, itemIndex) => {
            return (
              <ReasonForVisitSuggestion
                key={item.name}
                input={inputValue}
                data={item}
                onClicked={() => handleClick(item)}
                isSelected={
                  categoryIndex === index &&
                  itemPosition >= 0 &&
                  itemPosition <= category.items.length - 1 &&
                  itemPosition === itemIndex
                }
              />
            );
          })}
        </div>
      );
    });
    return <div className={styles.UnfilteredResults}>{results}</div>;
  };

  const searchResults = () => {
    if (selectedSpecialty && selectedSpecialty.subItems) {
      return specialtyResults(selectedSpecialty.subItems);
    } else if (suggestions.length > 0 || suggestedProviders.length > 0) {
      return (
        <>
          {suggestionResults(suggestions)}
          {providerResults(suggestedProviders)}
        </>
      );
    } else if (inputValue.length > 0) {
      return <ReasonForVisitEmptyState inputString={inputValue} />;
    } else {
      return initialResults();
    }
  };

  return (
    <>
      <NavBar
        view={selectedSpecialty !== undefined ? View.Form : View.Login}
        onBackButtonClicked={() => {
          setSelectedSpecialty(null);
          setInputValue('');
          setCursor(-1);
          setSuggestions([]);
          setCurrentCategories(initialCategories);
          setSuggestedProviders([]);
        }}
      />
      <div className={styles.ReasonForVisitBorder}>
        <div className={styles.RFV}>
          <div className={styles.RFVBody}>
            <div className={styles.ReasonForVisitContainer}>
              <div className={styles.Label}>
                {selectedSpecialty
                  ? `Please tell us the reason for your ${selectedSpecialty.name} appointment`
                  : `Add the reason for your visit to help us find available providers`}
              </div>
              <TextInput
                className={styles.ReasonForVisitInput}
                autoFocus={true}
                onKeyDown={handleKeyPress}
                placeholder={
                  selectedSpecialty
                    ? `Search ${selectedSpecialty.name} visit reasons`
                    : `Search all appointment reasons`
                }
                onChange={updateInputValue}
                value={inputValue}
                InputProps={{
                  className: styles.ReasonForVisitInput,
                  startAdornment: (
                    <InputAdornment position="start">
                      <img
                        style={{ marginLeft: `1rem` }}
                        src={String(searchIcon)}
                        alt="search"
                      />
                    </InputAdornment>
                  ),
                  color: `primary`,
                }}
              />
              <div className={styles.DropDownWrapper}>{searchResults()}</div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default ReasonForVisitSelector;
